import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Modal } from 'antd';
import useSheetAnim from '../hooks/useSheetAnim';
import useIsDesktop from '../hooks/useIsDesktop';
import { useWhatsNew } from '../context/WhatsNewContext';
import { useInstallPrompt } from '../context/InstallPromptContext';
import { usePushPrompt } from '../context/PushPromptContext';
import { useAuth } from '../context/AuthContext';
import { WHATS_NEW_PATH } from '../lib/releaseNotes';
import { useMemoGateOpen } from '../lib/memoGateStore';

/**
 * WhatsNew — the short pop-up shown once after the app updates itself: the
 * headlines of what the agent has not seen yet, and "See what's new" to the
 * What's new page (pages/WhatsNewPage), which holds every release in full with
 * its demo clips. The page is always on More (the avatar menu on desktop).
 *
 * Mounted once at app root, like the install and push sheets, because the update
 * it announces can land on ANY screen — the service worker swaps the build in the
 * background, so there is no one page an agent must pass through to be told.
 *
 * Copy lives in lib/releaseNotes.js; this only decides when to say it.
 */
export default function WhatsNew() {
  const { unseen, markSeen } = useWhatsNew();
  const navigate = useNavigate();
  const { showPrompt: installSheetOpen } = useInstallPrompt() || {};
  const { showPrompt: pushSheetOpen } = usePushPrompt() || {};
  const { isAuthed } = useAuth() || {};
  const { pathname } = useLocation();
  const desktop = useIsDesktop();
  // A memo from HQ the agent must confirm comes first; the notes wait for it.
  const memoOpen = useMemoGateOpen();
  const [visible, setVisible] = useState(false);

  // Notes are for agents inside the app: a customer on a public signing link is
  // not one, and the login and recovery screens are not the place either.
  // Anything unsigned-in also waits — the notes describe screens they cannot see
  // yet, and an agent who signs in on a colleague's phone should not be the one
  // who spends the announcement.
  const suppressed =
    !isAuthed
    || pathname.startsWith('/sign/')
    || pathname.startsWith('/join/')
    || pathname === '/login'
    || pathname === '/reset-password'
    // The page itself marks the release seen; nothing to announce over it.
    || pathname === WHATS_NEW_PATH;

  // Waits for the screen behind it to paint, and gives way to the install and
  // push sheets: all three are one-shot nudges, and stacking them turns a first
  // launch into a queue of dialogs. What's new is the one that can safely wait
  // for the next launch, so it yields.
  const owed = unseen.length > 0;
  useEffect(() => {
    if (!owed || suppressed) { setVisible(false); return undefined; }
    if (installSheetOpen || pushSheetOpen || memoOpen) { setVisible(false); return undefined; }
    const t = setTimeout(() => setVisible(true), 1200);
    return () => clearTimeout(t);
  }, [owed, suppressed, installSheetOpen, pushSheetOpen, memoOpen]);

  const { mounted, closing } = useSheetAnim(visible && !desktop);

  // What is on screen is captured at the moment it opens. Closing acknowledges
  // the release, which empties `unseen` in the same tick — read live, the sheet
  // would blank out mid-exit and the modal would fade on an empty box.
  const [shown, setShown] = useState(null);
  useEffect(() => {
    if (visible && unseen.length > 0) setShown(unseen);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [visible]);

  const handleClose = () => {
    setVisible(false);
    markSeen();
  };

  const seeAll = () => {
    handleClose();
    navigate(WHATS_NEW_PATH);
  };

  if (!shown) return null;

  const body = <Headlines releases={shown} />;

  if (desktop) {
    return (
      <Modal
        open={visible}
        onCancel={handleClose}
        centered
        width={520}
        title={<span style={{ fontSize: 15.5, fontWeight: 650 }}>What’s new</span>}
        footer={[
          <Button key="close" onClick={handleClose}>Close</Button>,
          <Button key="all" type="primary" onClick={seeAll}>See what’s new</Button>,
        ]}
        styles={{ body: { maxHeight: '62vh', overflowY: 'auto', paddingTop: 4 } }}
      >
        {body}
      </Modal>
    );
  }

  if (!mounted) return null;

  return (
    <div
      onClick={handleClose}
      className={`sheet-scrim${closing ? ' is-closing' : ''}`}
      style={{ position: 'fixed', inset: 0, zIndex: 4600, background: 'rgba(0,0,0,.45)', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={`sheet-panel${closing ? ' is-closing' : ''}`}
        style={{ width: '100%', maxWidth: 460, margin: '0 auto', background: 'var(--surface)', borderRadius: '18px 18px 0 0', maxHeight: '88%', display: 'flex', flexDirection: 'column' }}
      >
        <div style={{ padding: '10px 16px 0', flex: 'none' }}>
          <div style={{ width: 38, height: 4, borderRadius: 2, background: 'var(--border)', margin: '0 auto' }} />
        </div>

        <div className="scrx" style={{ flex: 1, overflowY: 'auto', padding: '16px 16px 4px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16 }}>
            <img src="/icon-192.png" alt="" style={{ width: 46, height: 46, borderRadius: 12, border: '1px solid var(--border)', flex: 'none' }} />
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 15.5, fontWeight: 600 }}>What’s new</div>
              <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 2 }}>
                CIDAS has updated itself to v{shown[0].version}
              </div>
            </div>
          </div>
          {body}
        </div>

        <div style={{ padding: '15px 16px', paddingBottom: 'calc(12px + env(safe-area-inset-bottom))', borderTop: '1px solid var(--border)', flex: 'none' }}>
          <div style={{ display: 'flex', gap: 10 }}>
            <Button block onClick={handleClose}>Close</Button>
            <Button block type="primary" onClick={seeAll}>See what’s new</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * The headlines of each release the agent missed — the full notes and their
 * clips are on the What's new page. The version line is always drawn: it is what
 * tells an agent which build they are now on.
 */
function Headlines({ releases }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {releases.map((r) => (
        <div key={r.version} style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            <span style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: .3, color: 'var(--primary)', background: 'var(--chip)', borderRadius: 50, padding: '3px 9px', flex: 'none' }}>
              v{r.version}
            </span>
            <span style={{ fontSize: 11.5, color: 'var(--muted)' }}>{r.date}</span>
            <span style={{ flex: 1, height: 1, background: 'var(--border)' }} />
          </div>
          {r.items.map((it) => (
            <div key={it.title} style={{ display: 'flex', gap: 11, alignItems: 'baseline' }}>
              <span style={{ width: 6, height: 6, borderRadius: 50, background: 'var(--primary)', flex: 'none', transform: 'translateY(-2px)' }} />
              <span style={{ flex: 1, fontSize: 13.5, lineHeight: 1.45 }}>{it.title}</span>
              {it.videos?.length > 0 && (
                <svg width="15" height="15" viewBox="0 0 24 24" aria-label="Has a video" style={{ flex: 'none', alignSelf: 'center' }}>
                  <circle cx="12" cy="12" r="10" fill="none" stroke="var(--primary)" strokeWidth="1.8" />
                  <path d="M10 8.5v7l6-3.5z" fill="var(--primary)" />
                </svg>
              )}
            </div>
          ))}
        </div>
      ))}
      <div style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.5 }}>
        The details and demo videos are on the What’s new page.
      </div>
    </div>
  );
}
